import {ObjectId} from 'bson';
import React, {useRef, useEffect, useState} from 'react';
import {
  Box,
  Flex,
  Input,
  Button,
  VStack,
  HStack,
  Text,
  Spinner,
} from '@chakra-ui/react';
import {SegmentsPanel} from './SegmentsPanel';
import {useAiChat} from '../hooks/useAiChat';

export const SegmentationChatInterface = ({taskId}: {taskId: ObjectId}) => {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [previousResponseId, setPreviousResponseId] = useState<string>();
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const isBootstrapped = useRef(false);

  const {messages, status, sendMessage} = useAiChat({
    path: 'segmentationChat',
    setPreviousResponseId,
  });

  // Check if user is at bottom of scroll container
  const isAtBottom = () => {
    if (!messagesContainerRef.current) {
      return false;
    }

    const {scrollTop, scrollHeight, clientHeight} =
      messagesContainerRef.current;
    return scrollTop + clientHeight >= scrollHeight - 20;
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (!shouldAutoScroll) {
      return;
    }

    const timeout = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    }, 0);

    return () => clearTimeout(timeout);
  }, [messages, shouldAutoScroll]);

  // Handle scroll events
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    let lastScrollTop = container.scrollTop;
    const handleScroll = () => {
      const currentScrollTop = container.scrollTop;
      const isScrollingUp = currentScrollTop < lastScrollTop;

      if (isAtBottom()) {
        setShouldAutoScroll(true);
      } else if (isScrollingUp) {
        // Only turn off auto-scroll when user scrolls up
        setShouldAutoScroll(false);
      }

      lastScrollTop = currentScrollTop;
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle user interactions (click)
  useEffect(() => {
    const handleClick = () => {
      setShouldAutoScroll(false);
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  // Reset auto-scroll when user submits a message
  const handleFormSubmit = (e: React.FormEvent) => {
    setShouldAutoScroll(true);
    e.preventDefault();
    sendMessage(
      {text: input},
      {
        body: {taskId, previousResponseId, mode: 'chat'},
      }
    );
    setInput('');
  };

  // Keep input focused after form submission
  useEffect(() => {
    if (status === 'ready' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [status]);

  // Bootstrap the chat with the initial message plus the taskId.
  useEffect(() => {
    if (isBootstrapped.current) {
      return;
    }

    isBootstrapped.current = true;

    sendMessage(
      {text: ''},
      {
        body: {taskId, mode: 'bootstrap'},
      }
    );
  }, [taskId, sendMessage]);

  const isLoading = status === 'streaming';
  return (
    <Flex h="100%" w="100%">
      {/* Main Chat Area */}
      <Flex direction="column" flex="1">
        {/* Messages Area */}
        <Box ref={messagesContainerRef} flex="1" overflowY="auto" p={4}>
          <VStack spacing={4} align="stretch">
            {messages.length === 0 ? (
              <Text color="gray.500" textAlign="center" mt={8}>
                Start a conversation by typing a message below.
              </Text>
            ) : (
              messages.map((message) => (
                <Box
                  key={message.id}
                  alignSelf={
                    message.role === 'user' ? 'flex-end' : 'flex-start'
                  }
                  maxW="70%"
                >
                  <Box
                    bg={message.role === 'user' ? 'blue.500' : 'gray.100'}
                    color={message.role === 'user' ? 'white' : 'black'}
                    p={3}
                    borderRadius="lg"
                    boxShadow="sm"
                  >
                    {message.parts.map((part, index) => (
                      <Text key={index} whiteSpace="pre-wrap">
                        {part.type === 'text' ? part.text : null}
                      </Text>
                    ))}{' '}
                    <Text
                      fontSize="xs"
                      color={message.role === 'user' ? 'blue.100' : 'gray.500'}
                      mt={1}
                    >
                      {new Date().toLocaleTimeString()}
                    </Text>
                  </Box>
                </Box>
              ))
            )}
            {isLoading && (
              <Box alignSelf="flex-start" maxW="70%">
                <Box bg="gray.100" p={3} borderRadius="lg" boxShadow="sm">
                  <HStack spacing={2}>
                    <Spinner size="sm" />
                    <Text>AI is thinking...</Text>
                  </HStack>
                </Box>
              </Box>
            )}
            {/* Invisible div to scroll to */}
            <div ref={messagesEndRef} />
          </VStack>
        </Box>

        {/* Input Area */}
        <Box p={4} borderTop="1px" borderColor="gray.200" bg="white">
          <form onSubmit={handleFormSubmit}>
            <HStack spacing={3}>
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                isDisabled={isLoading || !previousResponseId}
                size="lg"
                borderRadius="lg"
              />
              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                isDisabled={!input.trim() || isLoading || !previousResponseId}
                borderRadius="lg"
              >
                Send
              </Button>
            </HStack>
          </form>
        </Box>
      </Flex>

      {/* Segments Panel */}
      <SegmentsPanel responseId={previousResponseId} />
    </Flex>
  );
};
